/**
 * The rover's deploy and recall: WASD handed to the vehicle, the chase camera
 * that frames it, and JUMP TO ROVER's own ease.
 */

import * as THREE from 'three';

import type { AtlasLive } from '../atlas';
import { track } from '../visits/bus.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { InputHandle } from './input.ts';
import type { PlotBuildHandle } from './plotBuild.ts';
import type { EventLogHandle } from './panels/eventLog.ts';
import type { BuildPanelHandle } from './panels/build.ts';
import type { TelemetryPanelHandle } from './panels/telemetry.ts';

export interface RoverDriveDeps {
  readonly devMode: boolean;
  readonly focusRig: FocusRigHandle;
  readonly overlay: HTMLElement;
  readonly solarScrub: SolarScrubHandle;
  readonly plotBuild: PlotBuildHandle;
  readonly eventLogHandle: EventLogHandle;
  readonly live: AtlasLive;
  syncTierToAltitude(): void;
  renderOverlay(): void;
  /** Built below this seam's call site in atlas.ts, so getters read at the press. */
  readonly buildPanel: BuildPanelHandle;
  readonly telemetryPanel: TelemetryPanelHandle;
  readonly cameraRig: CameraRigHandle;
  readonly input: InputHandle;
}

/** The drive's own state that the panels, the camera rig, the tick and the probe read or write. */
export interface RoverDriveState {
  readonly driving: boolean;
  followRover: boolean;
  jumpEaseT: number | null;
  jumpEaseFromYaw: number;
  jumpEaseFromPitch: number;
  jumpEaseFromTilt: number;
  jumpEaseFromAlt: number;
}

export interface RoverDriveHandle {
  setDriving(on: boolean): Promise<void>;
  readonly JUMP_EASE_S: number;
  readonly jumpEaseFromLook: THREE.Vector3;
  readonly roverFocus: THREE.Vector3;
  readonly state: RoverDriveState;
}

export function createRoverDrive(deps: RoverDriveDeps): RoverDriveHandle {
  const { devMode, overlay, syncTierToAltitude, renderOverlay } = deps;
  const { focusCam } = deps.focusRig;
  const { solar } = deps.solarScrub;
  const { ensurePlotMesh } = deps.plotBuild;
  const { logEvent } = deps.eventLogHandle;

  // ── the rover ───────────────────────────────────────────────────────────
  //
  // Deploying it takes WASD over: the same keys that fly the camera now drive
  // the vehicle, and the camera becomes a chase. Rebinding rather than adding a
  // second scheme, because two movement schemes live at once is how you get a
  // player pressing W and watching nothing they expected happen.
  let driving = false;
  // Whether the camera is following the rover, separate from whether it is
  // deployed: re-entering SURFACE with the rover still out used to re-engage
  // this in one frame off whatever the ladder had aimed at, which is the whip
  // the owner reported. Deploy turns it on; leaving SURFACE turns it
  // off; only JUMP TO ROVER turns it back on once re-entered.
  let followRover = false;
  let deployedAtMs = 0;
  /** JUMP TO ROVER's own ease, seconds since the press; null when not easing. */
  let jumpEaseT: number | null = null;
  const JUMP_EASE_S = 0.6;
  const jumpEaseFromLook = new THREE.Vector3();
  let jumpEaseFromYaw = 0;
  let jumpEaseFromPitch = 0;
  let jumpEaseFromTilt = 0;
  let jumpEaseFromAlt = 0;
  const roverFocus = new THREE.Vector3();
  const roverBtn = overlay.querySelector('#pc-rover') as HTMLButtonElement;

  async function setDriving(on: boolean): Promise<void> {
    if (on && !deps.live.plot) {
      // Deploying IS entering the walkaround: the rover drives on the whole-file
      // plot, which is the terrain below the SURFACE edge and is not in the
      // scene above it. So the click takes the camera down there and waits for
      // the mesh rather than refusing.
      focusCam.setAltitude(200);
      deps.live.cameraDirty = true;
      syncTierToAltitude();
      await ensurePlotMesh();
      if (deps.live.disposed) return;
    }
    driving = on && !!deps.live.plot;
    roverBtn.classList.toggle('on', driving);
    roverBtn.innerHTML = driving ? '&#9632; RECALL ROVER' : '&#9658; DEPLOY ROVER';
    deps.buildPanel.skinRow.classList.toggle('hidden', !driving || !devMode);
    deps.telemetryPanel.autoRow.classList.toggle('hidden', !driving);
    deps.telemetryPanel.jumpRow.classList.toggle('hidden', !driving);
    if (!driving) {
      deps.buildPanel.creditEl.classList.add('hidden');
      deps.telemetryPanel.telemEl.classList.add('hidden');
      deps.telemetryPanel.auto = false;
      deps.telemetryPanel.autoBtn.classList.remove('on');
      deps.telemetryPanel.autoBtn.innerHTML = '&#9654; AUTO SURVEY';
    }
    if (driving) {
      const r = deps.live.plot!.deployRover();
      deployedAtMs = performance.now();
      track('rover_deploy', { body_id: focusCam.state.bodyId });
      // Hand the rover the sub-plots so it can track cell crossings.
      r.setSubPlots(deps.live.plotSubs);
      r.onSurvey = (cellId, cell) => {
        logEvent(`Survey: cell ${cellId.slice(-8)} confirmed tier 2`, cell);
        // Repaint the overlay to reflect the new tier.
        if (deps.live.plot && deps.live.plotField) {
          const rings = deps.live.plotField.placements.length > 4 ? 2 : 3;
          deps.live.plot.measure(rings, solar.lsDeg);
        }
        renderOverlay();
        deps.live.needsRender = true;
      };
      // Restore survey state from previous sessions and survey the landing cell.
      r.applySurveyState();
      // Frame it from behind and a little above, in METRES.
      //
      // Scene units are normalised to the cluster extent, so a fixed 0.030 is
      // 70 m on a single plot and 540 m across a 36-plot region — which is how
      // the first attempt deployed a rover nobody could see. Twelve metres back
      // is twelve metres back at any tier.
      // Twelve metres back. This used to be floored off the near plane, which
      // was `SCENE_NEAR` in plot units and so tens of metres at a region's
      // extent; the near plane is `SCENE_NEAR` in real metres now, half a
      // millimetre, so the floor was reading a number that could never bind.
      focusCam.setAltitude(12);
      // Rover heading is (sin yaw, cos yaw); camera forward, flattened, is
      // (-sin Y, cos Y). Behind the rover means those agree, so Y = -yaw.
      focusCam.setOrientation(-r.state.yaw, 0.30);
      deps.cameraRig.plotTilt = 0;
      deps.live.cameraDirty = true;
      followRover = true;
      jumpEaseT = null;
    } else {
      const odometerM = deps.live.plot?.rover?.odometerM ?? 0;
      if (odometerM > 0) track('rover_drive', { body_id: focusCam.state.bodyId, odometer_m: Math.round(odometerM / 10) * 10, seconds: Math.round((performance.now() - deployedAtMs) / 1000) });
      deps.live.plot?.recallRover();
      followRover = false;
      // An interrupted JUMP TO ROVER cancels mid-ease, and the ease is the only
      // thing spending `plotTilt` down toward the rover's own framing (0): cut
      // short, it would otherwise leave whatever fraction the ease had reached.
      // `descent-dojo --lookup`'s `look-jump-reset` skips this with `skipJumpTiltReset`.
      if (jumpEaseT !== null && !deps.cameraRig.lookUpDebug.skipJumpTiltReset) deps.cameraRig.plotTilt = 0;
      jumpEaseT = null;
    }
    deps.input.held.clear();
    renderOverlay();
    deps.live.needsRender = true;
  }
  roverBtn.addEventListener('click', () => { void setDriving(!driving); });

  return {
    setDriving,
    JUMP_EASE_S,
    jumpEaseFromLook,
    roverFocus,
    state: {
      get driving() { return driving; },
      get followRover() { return followRover; },
      set followRover(v) { followRover = v; },
      get jumpEaseT() { return jumpEaseT; },
      set jumpEaseT(v) { jumpEaseT = v; },
      get jumpEaseFromYaw() { return jumpEaseFromYaw; },
      set jumpEaseFromYaw(v) { jumpEaseFromYaw = v; },
      get jumpEaseFromPitch() { return jumpEaseFromPitch; },
      set jumpEaseFromPitch(v) { jumpEaseFromPitch = v; },
      get jumpEaseFromTilt() { return jumpEaseFromTilt; },
      set jumpEaseFromTilt(v) { jumpEaseFromTilt = v; },
      get jumpEaseFromAlt() { return jumpEaseFromAlt; },
      set jumpEaseFromAlt(v) { jumpEaseFromAlt = v; },
    },
  };
}
